import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import { TeamLeaderListItem } from "../Dto/TeamLeaderModel";
import { SearchTeamLeaderRequest } from "../Dto/TeamLeaderSearchRequest";
import { SearchTeamLeaderResponse } from "../Dto/TeamLeaderSearchResponse";
import { CreateTeamLeaderRequest } from "../Dto/TeamLeaderCreateRequest";
import { UpdateTeamLeaderRequest } from "../Dto/TeamLeaderUpdateRequest";

@Injectable({ providedIn: "root" })
export class TeamLeaderApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchTeamLeaders(params: SearchTeamLeaderRequest): Promise<SearchTeamLeaderResponse> {
    const url = new URL("team-leaders", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) {
      searchParams.set("search", params.search);
    }

    if (params.partnerIds) {
      for (const id of params.partnerIds) {
        searchParams.append("partnerIds[]", id);
      }
    }

    if (params.createdAtFrom) {
      searchParams.set("createdAtFrom", params.createdAtFrom);
    }

    if (params.createdAtTo) {
      searchParams.set("createdAtTo", params.createdAtTo);
    }

    if (params.page !== undefined) {
      searchParams.set("page", params.page.toString());
    }

    if (params.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    return await firstValueFrom(this.http.get<SearchTeamLeaderResponse>(url.toString(), { withCredentials: true }));
  }

  async getTeamLeaderById(id: string): Promise<TeamLeaderListItem | null> {
    const url = new URL(`team-leaders/${id}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.get<TeamLeaderListItem | null>(url.toString(), { withCredentials: true }));
  }

  async createTeamLeader(payload: CreateTeamLeaderRequest): Promise<TeamLeaderListItem> {
    const url = new URL("team-leaders", `${this.baseUrl}/`);
    const createData: CreateTeamLeaderRequest = {
      name: payload.name,
      address: payload.address,
    };

    if (payload.email !== undefined) createData.email = payload.email;
    if (payload.phone !== undefined) createData.phone = payload.phone;
    if (payload.partners !== undefined) createData.partners = payload.partners;

    return await firstValueFrom(this.http.post<TeamLeaderListItem>(url.toString(), createData, { withCredentials: true }));
  }

  async updateTeamLeader(id: string, payload: UpdateTeamLeaderRequest): Promise<TeamLeaderListItem | null> {
    const url = new URL(`team-leaders/${id}`, `${this.baseUrl}/`);
    const updateData: UpdateTeamLeaderRequest = {};

    if (payload.name !== undefined) updateData.name = payload.name;
    if (payload.email !== undefined) updateData.email = payload.email;
    if (payload.phone !== undefined) updateData.phone = payload.phone;
    if (payload.address !== undefined) updateData.address = payload.address;
    if (payload.partners !== undefined) updateData.partners = payload.partners;

    return await firstValueFrom(this.http.put<TeamLeaderListItem | null>(url.toString(), updateData, { withCredentials: true }));
  }

  async deleteTeamLeader(id: string): Promise<void> {
    const url = new URL(`team-leaders/${id}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async assignPartners(id: string, partnerIds: string[]): Promise<TeamLeaderListItem | null> {
    const url = new URL(`team-leaders/${id}/partners`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<TeamLeaderListItem | null>(url.toString(), { partnerIds }, { withCredentials: true }));
  }
}
